<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";
import VueSelect from "vue3-select-component";
import type { Option } from "vue3-select-component";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";

import { postClaimMissingPurchase } from "@/api";
import { useProducts } from "@/composables/api/useProducts";
import { useUser } from "@/composables/api/useUser";

import Button from "@/components/ui/Button.vue";

const emit = defineEmits<{
  (e: "sent"): void;
  (e: "close"): void;
}>();

const { data: user } = useUser();

const { t } = useI18n();
const route = useRoute();
const queryClient = useQueryClient();
const { data: products } = useProducts();

const isLoading = ref(false);
const value = ref("");

const options = computed<Option<string>[]>(() =>
  products.value
    ? products.value.map((product) => ({
        label: product.name,
        value: product.ean,
      }))
    : [],
);

async function handleSubmit() {
  const receiptId = route.params.id;

  if (isLoading.value || typeof receiptId !== "string" || !value.value) {
    return;
  }

  isLoading.value = true;

  try {
    await postClaimMissingPurchase(receiptId, value.value);
    queryClient.invalidateQueries({ queryKey: ["receipt", receiptId] });
    toast(t("toast.claimMissingProductSuccess"), { position: "top-center" });
    emit("sent");
    emit("close");
  } catch (error) {
    console.error(error);
  }

  isLoading.value = false;
}
</script>

<template>
  <div class="flex w-full flex-col gap-16">
    <div class="flex flex-col gap-4">
      <h1
        :class="
          twMerge(
            'text-galbani text-2xl font-semibold',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        {{ $t("dialog.missingProductClaim.title") }}
      </h1>

      <VueSelect
        v-model="value"
        teleport="body"
        :options="options"
        :isClearable="false"
        :placeholder="$t('dialog.missingProductClaim.placeholder')"
      />
    </div>

    <Button variant="secondary" :disabled="!value.length" @click="handleSubmit">
      {{ $t("dialog.missingProductClaim.button") }}
    </Button>
  </div>
</template>

<style lang="css" scoped>
:deep(.vue-select) {
  --vs-menu-height: 130px;
  --vs-padding: 4px 20px;
  --vs-text-color: #5c5c5c;
  --vs-font-weight: 500;
  --vs-outline-color: #214c9f;
  --vs-option-font-size: 15px;
  --vs-option-padding: 6px 10px;
  --vs-option-focused-background-color: #d7e1f4;
  --vs-option-selected-background-color: #214c9f;
  --vs-option-selected-text-color: #fff;
}

:deep(.vue-select .control) {
  min-height: 50px;
}
</style>

<style lang="css">
.menu[role="listbox"] {
  position: fixed !important;
  z-index: 10 !important;
  pointer-events: auto !important;
}
</style>
